import React, { useState } from "react";
import { Box, Typography, TextField, InputAdornment, Chip, Tooltip } from "@mui/material";
import { useDrag } from "react-dnd";
import SearchIcon from "@mui/icons-material/Search";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import EmailIcon from "@mui/icons-material/Email";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import DoneOutlineIcon from "@mui/icons-material/DoneOutline";
import DialpadIcon from "@mui/icons-material/Dialpad";
import GradeIcon from "@mui/icons-material/Grade";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import HdrStrongIcon from "@mui/icons-material/HdrStrong";
import PublishIcon from "@mui/icons-material/Publish";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import RecentActorsIcon from "@mui/icons-material/RecentActors";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import KeyIcon from "@mui/icons-material/Key";
import FlipToFrontIcon from "@mui/icons-material/FlipToFront";
import RecommendIcon from "@mui/icons-material/Recommend";
import LensIcon from "@mui/icons-material/Lens";
import { useAuth } from "../../../context/AuthContext";
import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import BookOnlineIcon from "@mui/icons-material/BookOnline";
import TagIcon from "@mui/icons-material/Tag";
import LockIcon from "@mui/icons-material/Lock";

const componentItems = [
  { label: "Question", defaultLabel: "What is your name?", icon: <QuestionMarkIcon />, type: "question", color: "#6366f1" },
  { label: "Single Choice", defaultLabel: "What is your gender?", icon: <DoneOutlineIcon />, type: "single_choice", color: "#f59e0b" },
  { label: "Email", defaultLabel: "Enter your email ID", icon: <EmailIcon />, type: "email_feild", color: "#10b981" },
  { label: "Multiple Choice", defaultLabel: "Select multiple options", icon: <DoneAllIcon />, type: "multiple_options", color: "#8b5cf6" },
  { label: "Mobile Number", defaultLabel: "Enter your mobile number", icon: <DialpadIcon />, type: "mobile_number", color: "#ec4899" },
  { label: "Number", defaultLabel: "Enter a number", icon: <TagIcon />, type: "number", color: "#06b6d4" },
  { label: "Rating", defaultLabel: "Give a rating", icon: <GradeIcon />, type: "rating", color: "#f97316" },
  { label: "Date Picker", defaultLabel: "Select a date", icon: <CalendarMonthIcon />, type: "date_picker", color: "#14b8a6" },
  { label: "Time Picker", defaultLabel: "Select a time", icon: <AccessTimeIcon />, type: "time_picker", color: "#6366f1" },
  { label: "Location", defaultLabel: "Enter your location", icon: <LocationOnIcon />, type: "location", color: "#ef4444" },
  { label: "Range", defaultLabel: "Choose a range", icon: <HdrStrongIcon />, type: "range", color: "#f59e0b" },
  { label: "File Upload", defaultLabel: "Upload your file", icon: <PublishIcon />, type: "file_upload", color: "#10b981" },
  { label: "Website", defaultLabel: "Enter website URL", icon: <DashboardCustomizeIcon />, type: "website", color: "#8b5cf6" },
  { label: "Ask Contacts", defaultLabel: "Select a contact", icon: <RecentActorsIcon />, type: "ask_contacts", color: "#ec4899", paid: true },
  { label: "Order Items", defaultLabel: "Order your items", icon: <ShoppingCartIcon />, type: "order_items", color: "#06b6d4", paid: true },
  { label: "Authenticator", defaultLabel: "Enter OTP", icon: <KeyIcon />, type: "authenticator", color: "#f97316", paid: true },
  { label: "Form", defaultLabel: "Fill this form", icon: <FlipToFrontIcon />, type: "form", color: "#14b8a6", paid: true },
  { label: "Carousel", defaultLabel: "View carousel options", icon: <RecommendIcon />, type: "carousel_with_buttons", color: "#ef4444", paid: true },
  { label: "Dynamic Question", defaultLabel: "Dynamic input", icon: <LensIcon />, type: "dynamic_question", color: "#6366f1", paid: true },
  { label: "Real Time Search", defaultLabel: "Search here", icon: <ContentPasteSearchIcon />, type: "Real_time_search", color: "#f59e0b", paid: true },
  { label: "Appointment", defaultLabel: "Book your appointment", icon: <BookOnlineIcon />, type: "appointment_booking", color: "#10b981", paid: true },
];

const getCategory = (type) => {
  const basic = ["question", "single_choice", "email_feild", "multiple_options", "mobile_number", "number"];
  const advanced = ["rating", "date_picker", "time_picker", "location", "range", "file_upload", "website"];
  if (basic.includes(type)) return "Basic";
  if (advanced.includes(type)) return "Advanced";
  return "Premium";
};

const DraggableItem = ({ item, locked }) => {
  const [{ isDragging }, dragRef] = useDrag(() => ({
    type: "COMPONENT",
    item,
    canDrag: !locked,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  }));

  const content = (
    <Box ref={locked ? null : dragRef}
      sx={{
        position: "relative",
        bgcolor: locked ? "rgba(243,244,246,0.8)" : "rgba(255,255,255,0.95)",
        border: `1px solid ${locked ? "rgba(209,213,219,0.5)" : "rgba(229,231,235,0.7)"}`,
        borderRadius: 2,
        p: { xs: 0.8, md: 1 },
        textAlign: "center",
        cursor: locked ? "not-allowed" : "grab",
        opacity: isDragging ? 0.3 : locked ? 0.6 : 1,
        transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
        "&:hover": locked ? {} : {
          bgcolor: `${item.color}08`,
          borderColor: `${item.color}50`,
          transform: "translateY(-2px) scale(1.02)",
          boxShadow: `0 6px 16px ${item.color}15`,
        },
        "&:active": locked ? {} : { transform: "scale(0.95)" },
        userSelect: "none",
      }}>
      {item.paid && (
        <LockIcon sx={{ position: "absolute", top: 3, right: 3, fontSize: 10, color: locked ? "#ef4444" : "#9ca3af" }} />
      )}
      <Box sx={{ fontSize: 20, lineHeight: 1, color: item.color, mb: 0.3 }}>{item.icon}</Box>
      <Typography sx={{ fontSize: { xs: 9, md: 10 }, fontWeight: 600, color: "#374151", lineHeight: 1.2 }}>
        {item.label}
      </Typography>
    </Box>
  );

  return locked ? (
    <Tooltip title="Upgrade your plan to unlock this component" arrow placement="top">
      {content}
    </Tooltip>
  ) : content;
};

const categories = ["Basic", "Advanced", "Premium"];
const catColors = { Basic: "#6366f1", Advanced: "#f59e0b", Premium: "#ec4899" };

const FlowSidebarComponent = () => {
  const { questionTypes } = useAuth();
  const [search, setSearch] = useState("");

  const filtered = search.trim()
    ? componentItems.filter((item) => item.label.toLowerCase().includes(search.toLowerCase()))
    : componentItems;

  return (
    <Box sx={{
      width: { xs: "100%", md: 240, lg: 260 },
      maxHeight: { xs: 200, md: "calc(100vh - 60px)" },
      overflowY: "auto",
      overflowX: "hidden",
      flexShrink: 0,
      background: "linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(249,250,251,0.9) 100%)",
      backdropFilter: "blur(12px)",
      borderRight: { xs: "none", md: "1px solid rgba(229,231,235,0.5)" },
      borderBottom: { xs: "1px solid rgba(229,231,235,0.5)", md: "none" },
      p: { xs: 1.5, md: 1.5 },
      "&::-webkit-scrollbar": { width: 4 },
      "&::-webkit-scrollbar-thumb": { bgcolor: "#e5e7eb", borderRadius: 2 },
    }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, px: 0.5 }}>
        <Box sx={{ width: 26, height: 26, borderRadius: "8px", background: "linear-gradient(135deg, #6366f1, #8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <DashboardCustomizeIcon sx={{ fontSize: 14, color: "#fff" }} />
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: 14, color: "#111827" }}>Components</Typography>
        <Chip label={componentItems.length} size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: "#f3f4f6", ml: "auto" }} />
      </Box>

      {/* Search */}
      <TextField
        placeholder="Search..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        size="small" fullWidth
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 15, color: "#9ca3af" }} /></InputAdornment> }}
        sx={{
          mb: 1.5,
          "& .MuiOutlinedInput-root": {
            borderRadius: 2, bgcolor: "#fff", fontSize: 12, height: 34,
            "& fieldset": { borderColor: "#e5e7eb" },
            "&.Mui-focused fieldset": { borderColor: "#6366f1", borderWidth: 1.5 },
          },
        }}
      />

      {/* Items */}
      {search.trim() ? (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(4,1fr)", sm: "repeat(5,1fr)", md: "repeat(3,1fr)" }, gap: 0.7 }}>
          {filtered.map((item, i) => <DraggableItem key={i} item={item} locked={item.paid && !questionTypes.includes(item.type)} />)}
          {filtered.length === 0 && <Typography sx={{ gridColumn: "1/-1", fontSize: 12, color: "#9ca3af", textAlign: "center", py: 2 }}>No results</Typography>}
        </Box>
      ) : (
        categories.map((cat) => {
          const items = componentItems.filter((i) => getCategory(i.type) === cat);
          return (
            <Box key={cat} sx={{ mb: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.7, px: 0.3 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: catColors[cat] }} />
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  {cat}
                </Typography>
                {cat === "Premium" && <LockIcon sx={{ fontSize: 10, color: "#9ca3af", ml: "auto" }} />}
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(4,1fr)", sm: "repeat(5,1fr)", md: "repeat(3,1fr)" }, gap: 0.7 }}>
                {items.map((item, i) => <DraggableItem key={i} item={item} locked={item.paid && !questionTypes.includes(item.type)} />)}
              </Box>
            </Box>
          );
        })
      )}
    </Box>
  );
};

export default FlowSidebarComponent;
